<script setup>
import axios from 'axios'
import { ref, computed, watch } from 'vue'
import { Head, router, usePage } from '@inertiajs/vue3'
import { PhUserCirclePlus } from "@phosphor-icons/vue"
import Layout from '@pages/Shared/layouts/StaffLayout.vue'
import Modal from '@pages/staff/components/StaffModal.vue'
import Table from '@pages/Shared/ui/Table.vue'
import Prompt from '@pages/Shared/Prompt.vue'
import { flashToast } from "@/toast.js"
import userMale from '@/../images/user-male.png'
import userFemale from '@/../images/user-female.png'
import { capitalize } from '@/helpers.js'

const props = defineProps({
    staff:    Object,
    roles:    Array,
    classes:  Array,
    subjects: Array,
    filters:  Object,
})

const openModal     = ref(false)
const modalStatus   = ref(null)
const modalTitle    = ref('')
const showPrompt    = ref(false)
const selectedStaff = ref(null)
const deleteId      = ref(null)
const isLoadingId   = ref(false)

const filters = ref({ search: null, gender: null, role: null, class: null, status: null, ...props.filters })

watch(() => props.filters, val => { filters.value = { ...val } }, { deep: true })

const roleOptions = computed(() =>
    (props.roles ?? []).map(r => ({ label: capitalize(r.name), value: r.id }))
);

const classOptions = computed(() =>
    (props.classes ?? []).map(c => ({ label: capitalize(c.name), value: c.id }))
)
const subjectOptions = computed(() =>
    (props.subjects ?? []).map(s => ({ label: capitalize(s.name), value: s.id }))
)

const columns = [
    { key: 'index',    label: '#',          type: 'index'   },
    { key: 'id',       label: 'ID'                          },
    { key: 'avatar',   label: 'Avatar',     type: 'slot'    },
    { key: 'name',     label: 'Name'                        },
    { key: 'email',    label: 'Email'                       },
    { key: 'gender',   label: 'Gender'                      },
    { key: 'phone',    label: 'Phone'                       },
    { key: 'address',  label: 'Address'                     },
    { key: 'roles',    label: 'Role(s)',    type: 'slot'    },
    { key: 'classes',  label: 'Class(es)', type: 'slot'    },  
    { key: 'subjects', label: 'Subject(s)', type: 'slot'   },
    { key: 'status',   label: 'Status',     type: 'slot'   },
    { key: 'actions',  label: '',           type: 'actions' },
]

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value)
            .filter(([_, v]) => v !== null && v !== undefined && v !== '')
    )
    router.get('/staff/manage-primary', { ...cleaned, filter: 1 }, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}

const handleSearch = (value) => { filters.value.search = value; applyFilters() }
const handleFilter = (newFilters) => { filters.value = { ...filters.value, ...newFilters }; applyFilters() }

const findRow = (id) => props.staff?.data?.find(s => s.id === id)

const editStaff = (id) => {
    const row = findRow(id)
    if (!row) return
    selectedStaff.value = row
    modalTitle.value    = 'Edit Staff'
    modalStatus.value   = 'edit'
    openModal.value     = true
}

const viewStaff = (id) => {
    const row = findRow(id)
    if (!row) return
    selectedStaff.value = row
    modalTitle.value    = 'View Staff'
    modalStatus.value   = 'view'
    openModal.value     = true
}

const openAddStaffModal = async () => {
    try {
        const res = await axios.post('/staff/manage-primary/get-id')
        selectedStaff.value = { id: res.data.id }
    } catch {
        selectedStaff.value = null
    } finally {
        modalTitle.value  = 'Add Staff'
        modalStatus.value = 'create'
        openModal.value   = true
    }
}

const deleteStaff = (id) => { deleteId.value = id; showPrompt.value = true }

const confirmDelete = () => {
    router.delete(`/staff/manage-primary/${deleteId.value}`, {
        onSuccess: (page) => { showPrompt.value = false; flashToast(page.props.flash) }
    })
}

const handleSubmit = (form) => {
    const options = {
        onSuccess: (page) => { openModal.value = false; flashToast(page.props.flash) }
    }
    if (modalStatus.value === 'edit') {
        form._method = 'put'
        form.post(`/staff/manage-primary/${encodeURIComponent(form.id)}`, options)
    } else {
        form.post('/staff/manage-primary', options)
    }
}

const page = usePage()
const can  = computed(() => page.props.can || {})
</script>

<template>
    <Head title="Manage Staff" />
    <Layout>

        <Prompt
            title="staff"
            :isShow="showPrompt"
            @cancel="showPrompt = false"
            @delete="confirmDelete"
        />

        <Modal
            v-model="openModal"
            :title="modalTitle"
            :status="modalStatus"
            :roles="roleOptions"
            :classRooms="classOptions"
            :subjects="subjectOptions"
            :staff="selectedStaff"
            @submitted="handleSubmit"
        />

        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Staff</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage all staff members</p>
            </div>
            <button
                v-if="can.createStaff"
                @click="openAddStaffModal"
                class="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                      text-white bg-primary hover:bg-primary-light rounded-lg transition"
            >
                <PhUserCirclePlus :size="20" />
                Add Staff
            </button>
        </div>

        <Table
            :data="staff"
            :columns="columns"
            variant="staff"
            row-key="id"
            :exportable="true"
            :actions="{ view: true, edit: true, delete: true }"
            :filters="[
                {
                    key: 'gender', label: 'Gender',
                    options: [
                        { label: 'All Genders', value: null },
                        { label: 'Male',        value: 'Male' },
                        { label: 'Female',      value: 'Female' },
                    ],
                    value: filters.gender,
                },
                {
                    key: 'role', label: 'Role',
                    options: [{ label: 'All Roles', value: null }, ...roleOptions],
                    value: filters.role,
                },
                {
                    key: 'class', label: 'Class',
                    options: [{ label: 'All Classes', value: null }, ...classOptions],
                    value: filters.class,
                },
                {
                    key: 'status', label: 'Status',
                    options: [
                        { label: 'All Statuses', value: null },
                        { label: 'Active',       value: 'active' },
                        { label: 'Inactive',     value: 'inactive' },
                    ],
                    value: filters.status,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
            @view="viewStaff"
            @edit="editStaff"
            @delete="deleteStaff"
        >
            <template #avatar="{ row }">
                <img
                    class="w-9 h-9 rounded-full object-cover ring-2 ring-gray-100"
                    :src="row.avatar
                        ? `/storage/${row.avatar}`
                        : (row.gender === 'Male' ? userMale : userFemale)"
                    :alt="row.name"
                />
            </template>

            <template #roles="{ row }">
                <div class="flex flex-wrap gap-1">
                    <span
                        v-for="role in row.roles" :key="role.id"
                        class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                               bg-primary-50 text-primary-700 rounded-md border border-primary-100"
                    >
                        {{ role.name }}
                    </span>
                </div>
            </template>

            <template #classes="{ row }">
                <div class="flex flex-wrap gap-1">
                    <template v-if="row.class_rooms?.length">
                        <span
                            v-for="cls in row.class_rooms" :key="cls.id"
                            class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                                bg-indigo-50 text-indigo-700 rounded-md border border-indigo-100"
                        >
                            {{ capitalize(cls.name) }}
                        </span>
                    </template>
                    <span v-else class="text-xs text-gray-400">N/A</span>
                </div>
            </template>

            <template #subjects="{ row }">
                <div class="flex flex-wrap gap-1">
                    <template v-if="row.subjects?.length">
                        <span
                            v-for="subject in row.subjects" :key="subject.id"
                            class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                                   bg-purple-50 text-purple-700 rounded-md border border-purple-100"
                        >
                            {{ subject.name }}
                        </span>
                    </template>
                    <span v-else class="text-xs text-gray-400">N/A</span>
                </div>
            </template>

            <template #status="{ row }">
                <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    :class="row.status?.toLowerCase() === 'active'
                        ? 'bg-green-50 text-green-700'
                        : 'bg-orange-50 text-orange-600'"
                >
                    <span
                        class="w-1.5 h-1.5 rounded-full"
                        :class="row.status?.toLowerCase() === 'active'
                            ? 'bg-green-500' : 'bg-orange-400'"
                    ></span>
                    {{ row.status }}
                </span>
            </template>
        </Table>

    </Layout>
</template>